import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, Volume2, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import VoiceChat from "@/components/VoiceChat";
import RightsCard from "@/components/RightsCard";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import CallTimer from "@/components/CallTimer";
import { RIGHTS } from "@/constants/testIds";

const SCENARIO_INFO = {
  door: { label: "ICE en mi puerta", accent: "red" },
  street: { label: "Calle o trabajo", accent: "amber" },
  detained: { label: "Estoy detenido", accent: "zinc" },
  phase2: { label: "Manual de Autoayuda", accent: "blue" },
  general: { label: "Información general", accent: "emerald" },
};

export default function Scenario() {
  const { scenario } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location.state || {};
  const [conversation, setConversation] = useState(null);
  const [rights, setRights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingAll, setPlayingAll] = useState(false);
  const [starterSent, setStarterSent] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const init = async () => {
      try {
        let convData;
        if (stateData.conversationId) {
          const r = await api.get(`/conversations/${stateData.conversationId}`);
          convData = r.data;
        } else {
          const conv = await api.post("/conversations", { scenario });
          convData = conv.data;
        }
        setConversation(convData);

        if (scenario === "door" || scenario === "street" || scenario === "detained" || scenario === "phase2") {
          const r = await api.get(`/rights/${scenario}`);
          setRights(r.data);
        }
      } catch (e) {
        if (e?.response?.status === 402) {
          const detail = e.response.data?.detail || "";
          const isDetention = detail === "no_balance_detention";
          toast.error(
            isDetention
              ? "Te quedaste sin llamadas de detención. Recarga para continuar."
              : "Te quedaste sin llamadas de emergencia. Recarga para continuar."
          );
          navigate(`/precios?reason=${detail || "no_balance"}`);
          return;
        }
        toast.error("No se pudo iniciar la sesión");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [scenario]);

  const readAllRights = async () => {
    if (!rights) return;
    setPlayingAll(true);
    try {
      const text = `${rights.title}. ${rights.rights.join(". ")}`;
      const { data } = await api.post("/voice/tts", { text });
      const blob = new Blob(
        [Uint8Array.from(atob(data.audio_base64), (c) => c.charCodeAt(0))],
        { type: "audio/mpeg" }
      );
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = () => setPlayingAll(false);
        audioRef.current.play();
      }
    } catch (e) {
      toast.error("Error al reproducir derechos");
      setPlayingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  const info = SCENARIO_INFO[scenario] || SCENARIO_INFO.general;
  const manual = stateData.manual;

  return (
    <div className="min-h-screen bg-zinc-50 pb-28">
      <div className="max-w-md mx-auto w-full px-5 pt-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-zinc-600 mb-4 hover:text-zinc-900"
          data-testid="scenario-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>

        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest font-bold text-red-600 mb-1">
            Sesión activa
          </p>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-900 leading-tight">
            {manual ? manual.title : info.label}
          </h1>
          {manual && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-800 bg-blue-100 px-2 py-1 rounded-full">
                <FileText className="w-3 h-3" />
                {manual.subtitle}
              </span>
              {manual.form_code && (
                <span className="text-xs font-bold text-zinc-700 bg-zinc-100 px-2 py-1 rounded-full">
                  {manual.form_code}
                </span>
              )}
            </div>
          )}
        </div>

        {conversation?.id && (
          <div className="mb-5">
            <CallTimer conversationId={conversation.id} />
          </div>
        )}

        {manual && manual.topics && (
          <div className="mb-6 bg-blue-50 border-2 border-blue-200 rounded-2xl p-4">
            <p className="text-xs font-black uppercase tracking-widest text-blue-800 mb-2">
              Temas que cubre este manual
            </p>
            <ul className="space-y-1.5">
              {manual.topics.map((t) => (
                <li key={t} className="text-sm text-zinc-800 flex gap-2">
                  <span className="text-blue-700 font-bold">·</span>
                  <span className="leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {rights && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-zinc-900">{rights.subtitle}</h2>
              <Button
                data-testid={RIGHTS.readAloud}
                onClick={readAllRights}
                disabled={playingAll}
                size="sm"
                variant="outline"
                className="h-10 rounded-lg border-2 border-zinc-300"
              >
                {playingAll ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Volume2 className="w-4 h-4 mr-1.5" />
                    Leer en voz
                  </>
                )}
              </Button>
            </div>
            <audio ref={audioRef} className="hidden" />
            <div className="flex flex-col gap-3">
              {rights.rights.map((r, i) => (
                <RightsCard key={`${r.slice(0,40)}-${i}`} index={i} text={r} />
              ))}
            </div>
          </div>
        )}

        <div className="border-t-2 border-zinc-200 pt-6 mt-2">
          <h2 className="text-lg font-bold text-zinc-900 mb-3">Habla con el asistente</h2>
          <p className="text-sm text-zinc-600 mb-4 leading-snug">
            Toca <span className="font-bold">Hablar</span>, di tu duda y suelta. El asistente responderá con voz.
          </p>
          {conversation && (
            <VoiceChat
              conversationId={conversation.id}
              scenario={scenario}
              autoStarter={!starterSent ? stateData.starter : null}
              onStarterSent={() => setStarterSent(true)}
            />
          )}
        </div>
      </div>
      <DisclaimerBanner />
    </div>
  );
}
